import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface Member {
  uid: string
  email: string
  displayName: string
}

interface DetectedItem {
  text: string
  done: boolean
  /** ネストされた詳細行（タスク化時にdescriptionへ入る） */
  details: string[]
}

/** アクション系見出しか判定（backend people.ts と同じ規則） */
function isActionHeading(line: string): boolean {
  return /^#{1,6}\s*.*(ネクストアクション|アクションアイテム|アクション|ToDo|TODO|タスク|Next\s*Action)/i.test(line)
}

const CHECKBOX_RE = /^\s*[-*]\s*\[( |x|X)\]\s*(.+)$/
const BULLET_RE = /^\s*[-*]\s+(.+)$/

/** 表示用にインラインMarkdown記法（**強調**・*斜体*・`コード`・[リンク]）を除去 */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .trim()
}

/** formatted.markdown からアクションアイテム行を抽出 */
export function detectActionItems(markdown: string): DetectedItem[] {
  if (!markdown) return []
  const items: DetectedItem[] = []
  const seen = new Set<string>()
  let underActionHeading = false
  let lastItem: DetectedItem | null = null
  for (const rawLine of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.replace(/\s+$/, '')
    if (!line.trim()) continue
    if (/^#{1,6}\s/.test(line.trim())) {
      underActionHeading = isActionHeading(line.trim())
      lastItem = null
      continue
    }
    // インデントされた行は直前アイテムの詳細として取り込む
    const indented = line.match(/^(\s{2,}|\t)[-*]?\s*(.+)$/)
    if (underActionHeading && lastItem && indented && !/^\d+\./.test(line.trim())) {
      const detail = stripInlineMarkdown(indented[2])
      if (detail) lastItem.details.push(detail)
      continue
    }
    const trimmed = line.trim()
    const checkbox = trimmed.match(CHECKBOX_RE)
    // 番号付きリスト（生成ルールのネクストアクション標準形式）
    const numbered = underActionHeading ? trimmed.match(/^\d+\.\s+(.+)$/) : null
    let text: string | null = null
    let done = false
    if (checkbox) {
      text = checkbox[2].trim()
      done = checkbox[1].toLowerCase() === 'x'
    } else if (numbered) {
      text = numbered[1].trim()
    } else if (underActionHeading) {
      const bullet = trimmed.match(BULLET_RE)
      if (bullet) text = bullet[1].trim()
    }
    if (text) text = stripInlineMarkdown(text)
    if (text && !seen.has(text)) {
      seen.add(text)
      lastItem = { text, done, details: [] }
      items.push(lastItem)
    }
  }
  return items
}

interface Props {
  documentId: string
  markdown: string
  participants: string[]
}

/**
 * 要約議事録タブ内の「検出されたアクションアイテム → タスク化」パネル。
 * 各行に「タスク化」ボタンを表示し、担当者・期限を指定してタスクを作成する。
 * 同じタイトル+出典のタスクが既に存在する行には ✓ を表示する。
 */
export function ActionItemTaskify({ documentId, markdown, participants }: Props) {
  const { user, firebaseUser } = useAuth()
  const [members, setMembers] = useState<Member[]>([])
  const [existingTitles, setExistingTitles] = useState<Set<string>>(new Set())
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [assigneeUid, setAssigneeUid] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  // 候補から外した項目（ドキュメントごとにlocalStorageへ永続化）
  const dismissKey = `bp-action-item-dismissed:${documentId}`
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(dismissKey) || '[]'))
    } catch {
      return new Set()
    }
  })
  const dismissItem = (text: string) => {
    setDismissed((prev) => {
      const next = new Set(prev)
      next.add(text)
      try {
        localStorage.setItem(dismissKey, JSON.stringify([...next]))
      } catch {
        // ignore
      }
      return next
    })
    setOpenIndex(null)
  }

  const items = useMemo(
    () => detectActionItems(markdown).filter((it) => !dismissed.has(it.text)),
    [markdown, dismissed]
  )

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  // 初回: 出典ドキュメントに紐づく既存タスク + 組織メンバーを取得
  useEffect(() => {
    if (!firebaseUser || !user?.orgId || items.length === 0) return
    let cancelled = false
    ;(async () => {
      try {
        const [tRes, mRes] = await Promise.all([
          authFetch(`/v1/tasks?sourceDocumentId=${encodeURIComponent(documentId)}`),
          authFetch(`/v1/organizations/${user.orgId}/members`)
        ])
        if (cancelled) return
        if (tRes.ok) {
          const data = await tRes.json()
          setExistingTitles(new Set((data.tasks || []).map((t: { title: string }) => t.title)))
        }
        if (mRes.ok) {
          const data = await mRes.json()
          setMembers(data.members || [])
        }
      } catch {
        // ベストエフォート
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, user?.orgId, documentId, items.length])

  // ポップオーバー外クリックで閉じる
  useEffect(() => {
    if (openIndex === null) return
    const onClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpenIndex(null)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [openIndex])

  /** アクション行のテキストから担当者候補（参加者名 ↔ メンバー）を推測 */
  const guessAssignee = (text: string): string => {
    const lower = text.toLowerCase()
    // 1) 行内にメンバー名/メールが含まれるか
    for (const m of members) {
      const tokens = [m.displayName, m.email, (m.email || '').split('@')[0]].filter(Boolean)
      if (tokens.some((t) => t.length >= 2 && lower.includes(t.toLowerCase()))) return m.uid
    }
    // 2) 参加者名がメンバー名に一致するか（行内に参加者名が含まれる場合）
    for (const p of participants) {
      if (p.length >= 2 && lower.includes(p.toLowerCase())) {
        const m = members.find((mm) =>
          (mm.displayName && (mm.displayName.includes(p) || p.includes(mm.displayName))) ||
          (mm.email && mm.email.toLowerCase().startsWith(p.toLowerCase()))
        )
        if (m) return m.uid
      }
    }
    return ''
  }

  const openPopover = (index: number) => {
    setError(null)
    setOpenIndex(index)
    setAssigneeUid(guessAssignee(items[index].text))
    setDueDate('')
  }

  const createTask = async (item: DetectedItem) => {
    if (creating) return
    setCreating(true)
    setError(null)
    try {
      const res = await authFetch('/v1/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: item.text,
          ...(item.details.length > 0 ? { description: item.details.join('\n') } : {}),
          ...(assigneeUid ? { assigneeUid } : {}),
          ...(dueDate ? { dueDate } : {}),
          sourceDocumentId: documentId
        })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `タスクの作成に失敗しました (${res.status})`)
      }
      setExistingTitles((prev) => new Set([...prev, item.text]))
      setOpenIndex(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'タスクの作成に失敗しました')
    } finally {
      setCreating(false)
    }
  }

  if (items.length === 0) return null

  return (
    <div className="card" style={{ padding: 12 }}>
      <button
        className="flex items-center gap-2 w-full text-left bg-transparent border-0 cursor-pointer p-0"
        onClick={() => setCollapsed(!collapsed)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, transform: collapsed ? 'rotate(-90deg)' : undefined, transition: 'transform .15s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
        <span className="text-sm font-medium text-primary">アクションアイテム候補</span>
        <span className="text-xs text-secondary">{items.length}件</span>
      </button>
      {!collapsed && (
        <div style={{ marginTop: 8 }}>
          {error && (
            <div className="alert danger" style={{ marginBottom: 8 }}>
              <div className="alert-body"><p className="text-xs">{error}</p></div>
            </div>
          )}
          {items.map((item, i) => {
            const exists = existingTitles.has(item.text)
            return (
              <div key={i} style={{ position: 'relative', borderTop: i > 0 ? '1px solid var(--color-border-light)' : undefined }}>
                <div className="flex items-center gap-2" style={{ padding: '6px 4px', minWidth: 0 }}>
                  <span
                    className="text-sm flex-1 truncate"
                    title={item.text}
                    style={{ minWidth: 0, ...(item.done ? { textDecoration: 'line-through', color: 'var(--color-text-secondary, #6b7280)' } : {}) }}
                  >
                    {item.text}
                  </span>
                  {exists ? (
                    <span className="text-xs flex items-center gap-1" style={{ color: 'var(--color-success-mid, #059669)', flexShrink: 0 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      タスク化済み
                    </span>
                  ) : (
                    <button className="btn btn-ghost btn-sm text-xs" style={{ flexShrink: 0 }} onClick={() => openPopover(i)}>
                      タスク化
                    </button>
                  )}
                  <button
                    className="btn-icon borderless"
                    title="候補から削除"
                    onClick={() => dismissItem(item.text)}
                    style={{ width: 20, height: 20, padding: 0, flexShrink: 0, color: 'var(--color-text-tertiary, #9ca3af)' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 12, height: 12 }}>
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {openIndex === i && (
                  <div
                    ref={popoverRef}
                    className="card"
                    style={{ position: 'absolute', right: 0, top: '100%', zIndex: 60, width: 320, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}
                  >
                    <div className="text-xs text-secondary" style={{ marginBottom: 8 }}>タスクを作成</div>
                    <div className="text-sm font-medium" style={{ marginBottom: item.details.length > 0 ? 4 : 10 }}>{item.text}</div>
                    {item.details.length > 0 && (
                      <div className="text-xs text-secondary" style={{ marginBottom: 10, whiteSpace: 'pre-wrap', maxHeight: 96, overflowY: 'auto' }}>
                        {item.details.join('\n')}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label className="text-xs text-secondary flex items-center gap-2">
                        担当者
                        <select className="form-input flex-1" value={assigneeUid} onChange={(e) => setAssigneeUid(e.target.value)}>
                          <option value="">自分</option>
                          {members.map((m) => (
                            <option key={m.uid} value={m.uid}>{m.displayName || m.email || m.uid}</option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs text-secondary flex items-center gap-2">
                        期限
                        <input type="date" className="form-input flex-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                      </label>
                      <div className="flex items-center gap-2 justify-end">
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenIndex(null)}>キャンセル</button>
                        <button className="btn btn-primary btn-sm" disabled={creating} onClick={() => createTask(item)}>
                          {creating ? <span className="spinner sm" /> : '作成'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
